import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Compass, User, BedDouble, Bath,
  CheckCircle, MapPin, Wand2, Gem, Share2, Copy, ChevronRight, Lock, Plus,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MatchScoreModal from '../../components/MatchScoreModal';
import BuyerIntakeWizard from './BuyerIntakeWizard';
import RefinementPanel from '../../components/RefinementPanel';
import ImportHomeModal from '../../components/ImportHomeModal';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:       '#0E1520',
  surface:  '#16202E',
  coral:    '#E07A5F',
  gold:     '#C4A87A',
  text:     '#F8F9FA',
  muted:    '#C8D3DF',
  border:   'rgba(255,255,255,0.08)',
  green:    'rgba(52,211,153,0.88)',
  amber:    'rgba(251,191,36,0.82)',
};
const serif     = { fontFamily: "'Playfair Display', Georgia, serif" };
const editorial = { fontFamily: "'Cormorant Garamond', 'Cormorant', Georgia, serif" };
const sans      = { fontFamily: "'Outfit', system-ui, sans-serif" };

const FEATURE_LABELS = {
  pool: 'Pool', solar: 'Solar', adu: 'ADU', no_hoa: 'No HOA',
  garage: 'Garage', ev_charger: 'EV Charger', office: 'Office',
  rv_parking: 'RV Parking', gated: 'Gated', fireplace: 'Fireplace',
};

const JOURNEY_STAGES_UNUSED = null;

// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatPrice(p) {
  if (!p && p !== 0) return '–';
  if (p >= 1_000_000) {
    const m = p / 1_000_000;
    return '$' + (m % 1 === 0 ? m.toFixed(0) : m.toFixed(2).replace(/0+$/, '')) + 'M';
  }
  if (p >= 1_000) return '$' + Math.round(p / 1_000) + 'K';
  return '$' + p;
}

function getMatchLabel(score) {
  if (score >= 90) return 'Strong Match';
  if (score >= 80) return "You'll like this";
  if (score >= 68) return 'Pretty close';
  if (score >= 55) return score + '% Match';
  return 'Possible match';
}

function getFeedbackLine(score) {
  if (score >= 85) return 'Feels like your style';
  if (score >= 72) return 'You tend to like homes like this';
  return null;
}

const FEATURE_OPTIONS = [
  { key: 'pool',       label: 'Pool' },
  { key: 'garage',     label: 'Garage' },
  { key: 'solar',      label: 'Solar Panels' },
  { key: 'adu',        label: 'ADU / Casita' },
  { key: 'ev_charger', label: 'EV Charger' },
  { key: 'no_hoa',     label: 'No HOA' },
  { key: 'office',     label: 'Home Office' },
  { key: 'gated',      label: 'Gated Entry' },
  { key: 'fireplace',  label: 'Fireplace' },
];

// ─── Match badge ───────────────────────────────────────────────────────────────
function MatchBadge({ score }) {
  const label = getMatchLabel(score);
  const color = score >= 80 ? C.coral : score >= 65 ? C.gold : 'rgba(200,211,223,0.7)';
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
      data-testid="match-badge"
      style={{ ...sans, background: 'rgba(12,18,28,0.82)', backdropFilter: 'blur(10px)', color, border: '1px solid ' + color + '40' }}>
      {label}
    </div>
  );
}

function getListingTags(listing) {
  const tags = [];
  if (listing.has_pool)       tags.push('Pool');
  if (listing.has_solar)      tags.push('Solar');
  if (listing.has_adu)        tags.push('ADU');
  if (listing.has_ev_charger) tags.push('EV Charger');
  if (!listing.has_hoa || listing.hoa_amount === 0) tags.push('No HOA');
  if (listing.has_garage)     tags.push('Garage');
  return tags.slice(0, 3);
}

// ─── Calibration buttons ───────────────────────────────────────────────────────
function CalibrationButtons({ onNotQuite, onClose, onJustRight, disabled }) {
  return (
    <div className="flex items-center justify-center gap-2 pt-3 pb-1">
      <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
        onClick={onNotQuite} disabled={disabled} data-testid="pass-btn"
        className="flex-1 py-2.5 rounded-full text-xs font-medium transition-all duration-200"
        style={{ ...sans, color: 'rgba(200,211,223,0.62)', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', letterSpacing: '0.01em' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; e.currentTarget.style.color = 'rgba(200,211,223,0.85)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(200,211,223,0.62)'; }}>
        Not quite
      </motion.button>
      <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
        onClick={onClose} disabled={disabled} data-testid="heart-btn"
        className="flex-1 py-2.5 rounded-full text-xs font-semibold transition-all duration-200"
        style={{ ...sans, color: 'rgba(200,211,223,0.85)', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', letterSpacing: '0.01em' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}>
        Close
      </motion.button>
      <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
        onClick={onJustRight} disabled={disabled} data-testid="ring-btn"
        className="flex-1 py-2.5 rounded-full text-xs font-semibold transition-all duration-200"
        style={{ ...sans, color: C.gold, background: 'rgba(196,168,122,0.1)', border: '1px solid rgba(196,168,122,0.3)', letterSpacing: '0.01em', boxShadow: '0 0 14px rgba(196,168,122,0.14)' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(196,168,122,0.18)'; e.currentTarget.style.boxShadow = '0 0 24px rgba(196,168,122,0.25)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(196,168,122,0.1)'; e.currentTarget.style.boxShadow = '0 0 14px rgba(196,168,122,0.14)'; }}>
        Just right
      </motion.button>
    </div>
  );
}

// ─── Listing card ──────────────────────────────────────────────────────────────
function ListingCard({ listing, isSaved, onSaveToggle, onShowScore, onView, onFeedback, onDismiss }) {
  const [cardStatus, setCardStatus]     = useState(null);
  const [showJustRight, setShowJustRight] = useState(false);
  const [microcopy, setMicrocopy]       = useState('');

  const photo     = listing.photos?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80';
  const reasons   = listing.match_reasons   || [];
  const conflicts = listing.match_conflicts || [];
  const feedbackLine = getFeedbackLine(listing.match_score);

  const handleNotQuite = (e) => {
    e.stopPropagation();
    setCardStatus('passing');
    onFeedback(listing.id, false, 'not_quite');
    setTimeout(() => onDismiss(listing.id), 420);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setCardStatus('close');
    setMicrocopy("You're getting close...");
    onSaveToggle(listing.id, isSaved);
    onFeedback(listing.id, true, 'close');
    setTimeout(() => setMicrocopy(''), 3000);
  };

  const handleJustRight = (e) => {
    e.stopPropagation();
    setCardStatus('justright');
    onSaveToggle(listing.id, false);
    onFeedback(listing.id, true, 'ring_high_intent');
    setTimeout(() => setShowJustRight(true), 300);
  };

  const isExiting   = cardStatus === 'passing';
  const isJustRight = cardStatus === 'justright';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isExiting
        ? { x: -60, opacity: 0, filter: 'saturate(0) brightness(0.6)', scale: 0.97 }
        : { opacity: 1, y: 0 }}
      transition={isExiting ? { duration: 0.4, ease: 'easeIn' } : { duration: 0.4 }}
      whileHover={!isExiting && !showJustRight ? { y: -2, boxShadow: '0 12px 40px rgba(0,0,0,0.45)' } : {}}
      className="relative rounded-2xl cursor-pointer group"
      style={{
        background: C.surface,
        border: isJustRight ? '1px solid rgba(196,168,122,0.45)' : '1px solid ' + C.border,
        boxShadow: isJustRight ? '0 0 40px rgba(196,168,122,0.18)' : 'none',
        transition: 'border-color 0.5s, box-shadow 0.5s',
      }}
      data-testid={'listing-card-' + listing.id}
      onClick={!showJustRight ? onView : undefined}>

      {/* Photo */}
      <div className="relative overflow-hidden rounded-t-2xl" style={{ height: '220px' }}>
        <img src={photo} alt={listing.address}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'; }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(14,21,32,0.95) 0%, rgba(14,21,32,0.35) 55%, transparent 100%)' }} />
        <div className="absolute top-3 left-3"><MatchBadge score={listing.match_score} /></div>
        <button
          onClick={e => { e.stopPropagation(); onShowScore({ ...listing, matchScore: listing.match_score, matchBreakdown: listing.match_breakdown, tags: (listing.met_features || []).map(f => FEATURE_LABELS[f] || f), dealbreakers: (listing.hit_dealbreakers || []).map(f => FEATURE_LABELS[f] || f) }); }}
          data-testid={'why-it-matches-' + listing.id}
          className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all hover:opacity-90"
          style={{ ...sans, background: 'rgba(14,21,32,0.75)', backdropFilter: 'blur(10px)', color: 'rgba(200,211,223,0.75)', border: '1px solid rgba(255,255,255,0.1)', letterSpacing: '0.01em' }}>
          See why &rarr;
        </button>
        <div className="absolute bottom-3 left-4 right-4">
          <p className="font-semibold text-sm leading-tight" style={{ ...sans, color: C.text }}>{listing.address}</p>
          <p className="text-xs mt-0.5" style={{ ...sans, color: 'rgba(200,211,223,0.65)' }}>{listing.city}, {listing.state}</p>
        </div>
      </div>

      {/* Card body */}
      <div className="px-4 pt-3 pb-4">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xl font-bold" style={{ ...serif, color: C.text }}>{formatPrice(listing.price)}</span>
          <div className="flex items-center gap-3 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.62)' }}>
            <span className="flex items-center gap-1"><BedDouble size={11} />{listing.beds}</span>
            <span className="flex items-center gap-1"><Bath size={11} />{listing.baths}</span>
            <span>{listing.sqft?.toLocaleString()} sqft</span>
          </div>
        </div>

        {/* Match breakdown */}
        {reasons.length > 0 && (
          <div className="mb-3 space-y-2.5">
            <div>
              <p className="mb-1.5" style={{ ...sans, color: 'rgba(52,211,153,0.45)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Checks your boxes</p>
              {reasons.slice(0, 2).map((r, i) => (
                <div key={i} className="flex items-center gap-2 mb-1" style={{ ...sans, color: C.green, fontSize: '0.8rem', lineHeight: 1.5 }}>
                  <span style={{ color: '#34D399', fontSize: '0.55rem' }}>&#10004;</span>{r}
                </div>
              ))}
            </div>
            {conflicts.length > 0 && (
              <div>
                <p className="mb-1.5" style={{ ...sans, color: 'rgba(251,191,36,0.4)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Worth noting</p>
                {conflicts.slice(0, 2).map((cf, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1" style={{ ...sans, color: C.amber, fontSize: '0.8rem', lineHeight: 1.5 }}>
                    <span style={{ color: '#FBBF24', fontSize: '0.55rem' }}>&#9702;</span>{cf}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {feedbackLine && cardStatus !== 'passing' && (
          <p className="mb-2 mt-1" style={{ ...sans, color: 'rgba(196,168,122,0.55)', fontSize: '0.72rem', fontStyle: 'italic' }}>
            {feedbackLine}
          </p>
        )}

        <AnimatePresence>
          {microcopy && (
            <motion.p initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="text-xs text-center mb-2 font-medium"
              style={{ ...sans, color: C.gold, fontStyle: 'italic' }}>
              {microcopy}
            </motion.p>
          )}
        </AnimatePresence>

        <CalibrationButtons
          onNotQuite={handleNotQuite}
          onClose={handleClose}
          onJustRight={handleJustRight}
          disabled={showJustRight || cardStatus === 'passing'}
        />
      </div>

      {/* Just right overlay */}
      <AnimatePresence>
        {showJustRight && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center z-50 overflow-hidden"
            style={{ background: 'rgba(10,16,26,0.94)', backdropFilter: 'blur(12px)' }}
            onClick={e => e.stopPropagation()}>
            <motion.div
              initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: [0.4, 1.2, 1], opacity: [0, 0.35, 0.12] }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 rounded-2xl"
              style={{ background: 'radial-gradient(circle at 50% 40%, rgba(196,168,122,0.5), transparent 70%)' }} />
            <motion.p
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: [0.97, 1.02, 1.0] }}
              transition={{ delay: 0.28, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 mb-1"
              style={{ ...editorial, color: C.gold, fontSize: '2.4rem', fontStyle: 'italic', fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1 }}>
              Just right.
            </motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.6 }}
              className="relative z-10 mb-8"
              style={{ ...sans, color: 'rgba(200,211,223,0.45)', fontSize: '0.75rem', letterSpacing: '0.02em' }}>
              This might be the one.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.55 }}
              className="relative z-10 flex flex-col gap-2.5 w-full px-6">
              <button onClick={e => { e.stopPropagation(); onView(); }}
                className="w-full py-3 rounded-full text-sm font-semibold transition-all hover:opacity-90"
                style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 20px rgba(224,122,95,0.3)' }}>
                View details
              </button>
              <button onClick={e => { e.stopPropagation(); setShowJustRight(false); }}
                className="w-full py-2.5 rounded-full text-xs font-medium transition-all hover:opacity-80"
                style={{ ...sans, color: 'rgba(200,211,223,0.6)', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)' }}>
                Save for later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Free-user usage tracking ──────────────────────────────────────────────────
const FREE_LIMITS = { views: 10, justRight: 1 };

function getUsage() {
  try { return JSON.parse(localStorage.getItem('gl_usage') || '{"views":0,"justRight":0}'); }
  catch { return { views: 0, justRight: 0 }; }
}
function setUsage(u) { localStorage.setItem('gl_usage', JSON.stringify(u)); }

function getSaveTypes() {
  try { return JSON.parse(localStorage.getItem('gl_save_types') || '{}'); }
  catch { return {}; }
}
function setSaveType(id, type) {
  const t = getSaveTypes(); t[id] = type; localStorage.setItem('gl_save_types', JSON.stringify(t));
}

function isPremium() { return localStorage.getItem('gl_premium') === '1'; }

const MICROCOPY = [
  "Finding your type…",
  "Your taste is emerging…",
  "Narrowing it down…",
  "Your taste is evolving…",
  "Getting warmer…",
  "You're getting close…",
  "Almost there…",
];

// ─── Paywall Modal ─────────────────────────────────────────────────────────────
function PaywallModal({ trigger, onClose }) {
  const copy = trigger === 'save'
    ? { pre: "You found one worth keeping.", cta: "Save your matches" }
    : trigger === 'justright'
    ? { pre: "Looks like you're getting close…", cta: "Unlock your matches" }
    : { pre: "You've seen the free matches.", cta: "See all your matches" };

  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: 'rgba(7,12,22,0.9)', backdropFilter: 'blur(18px)' }}
      onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-sm w-full rounded-3xl p-8 text-center overflow-hidden"
        data-testid="paywall-modal"
        style={{ background: C.surface, border: '1px solid rgba(196,168,122,0.22)' }}
        onClick={e => e.stopPropagation()}>

        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(196,168,122,0.12), transparent 70%)' }} />

        <div className="relative z-10">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'rgba(196,168,122,0.12)', border: '1px solid rgba(196,168,122,0.25)' }}>
            <Lock size={18} style={{ color: C.gold }} />
          </div>

          <p className="mb-2 font-semibold text-lg leading-snug"
            style={{ ...editorial, color: C.text, fontStyle: 'italic', fontSize: '1.45rem', letterSpacing: '-0.01em' }}>
            {copy.pre}
          </p>
          <p className="mb-7 text-sm leading-relaxed"
            style={{ ...sans, color: 'rgba(200,211,223,0.62)', lineHeight: 1.85 }}>
            Save your matches. Come back to them.<br />Find the one without losing it.
          </p>

          <Link to="/upgrade"
            className="block w-full py-3.5 rounded-full font-semibold text-sm mb-3 transition-all hover:opacity-90"
            style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 24px rgba(224,122,95,0.3)' }}>
            {copy.cta}
          </Link>
          <button onClick={onClose}
            className="block w-full py-2.5 text-xs transition-all"
            style={{ ...sans, color: 'rgba(200,211,223,0.45)', background: 'none', border: 'none' }}>
            Keep browsing free
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Share Sheet ────────────────────────────────────────────────────────────────
function ShareSheet({ listing, onClose }) {
  const [copied, setCopied] = useState(false);
  const url  = `${window.location.origin}/listing/${listing.id}`;
  const text = "This one feels like me… what do you think?";

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  const shareText = () => {
    const sms = `sms:?body=${encodeURIComponent(text + ' ' + url)}`;
    window.open(sms);
  };
  const shareEmail = () => {
    window.open(`mailto:?subject=Check this out&body=${encodeURIComponent(text + '\n\n' + url)}`);
  };
  const nativeShare = () => {
    if (navigator.share) navigator.share({ title: 'Goldielocks Match', text, url });
  };

  const actions = [
    { label: copied ? 'Copied!' : 'Copy link', icon: <Copy size={14} />, fn: copyLink },
    { label: 'Send as text', icon: <Share2 size={14} />, fn: shareText },
    { label: 'Send by email', icon: <Share2 size={14} />, fn: shareEmail },
    ...(navigator.share ? [{ label: 'Share…', icon: <Share2 size={14} />, fn: nativeShare }] : []),
  ];

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: 'rgba(7,12,22,0.85)', backdropFilter: 'blur(14px)' }}
      onClick={onClose}>
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-3xl p-6"
        style={{ background: C.surface, border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={e => e.stopPropagation()}>
        <p className="mb-1 text-sm font-semibold text-center" style={{ ...sans, color: C.text }}>Share this home</p>
        <p className="mb-5 text-xs text-center italic" style={{ ...sans, color: 'rgba(200,211,223,0.45)' }}>
          "This one feels like me… what do you think?"
        </p>
        <div className="space-y-2">
          {actions.map((a, i) => (
            <button key={i} onClick={a.fn}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-sm font-medium text-left transition-all hover:bg-white/5"
              style={{ ...sans, color: C.muted, border: '1px solid rgba(255,255,255,0.08)' }}>
              <span style={{ color: C.gold }}>{a.icon}</span>{a.label}
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Imported home card ───────────────────────────────────────────────────────
function ImportedCard({ imported: imp, onDelete }) {
  const ex      = imp.extracted || {};
  const score   = imp.match_score || 0;
  const reasons = imp.match_reasons || [];
  const photo   = (ex.photos || [])[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80';
  const color   = score >= 80 ? C.coral : score >= 65 ? C.gold : 'rgba(200,211,223,0.5)';
  const label   = score >= 80 ? 'Strong match' : score >= 65 ? 'Pretty close' : 'Possible match';

  const typeIcon = { url: '&#128279;', screenshot: '&#128247;', manual: '&#9998;' };

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden"
      style={{ background: C.surface, border: '1px solid rgba(196,168,122,0.15)' }}
      data-testid={'imported-card-' + imp.id}>

      <div className="relative" style={{ height: '150px' }}>
        <img src={photo} alt="imported listing" className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'; }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(14,21,32,0.95) 0%, transparent 55%)' }} />

        {/* Source badge */}
        <div className="absolute top-2.5 left-3 px-2 py-1 rounded-full text-xs"
          style={{ ...sans, background: 'rgba(14,21,32,0.82)', backdropFilter: 'blur(8px)', color: 'rgba(200,211,223,0.5)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.6rem' }}>
          <span dangerouslySetInnerHTML={{ __html: typeIcon[imp.source_type] || '&#9998;' }} /> You added this
        </div>

        {/* Match score */}
        <div className="absolute top-2.5 right-3 px-2.5 py-1 rounded-full text-xs font-semibold"
          style={{ ...sans, background: 'rgba(12,18,28,0.82)', backdropFilter: 'blur(8px)', color, border: '1px solid ' + color + '40' }}>
          {label}
        </div>

        <div className="absolute bottom-2.5 left-3">
          <p className="font-semibold text-xs" style={{ ...sans, color: C.text }}>
            {ex.address || 'Imported home'}
          </p>
          <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.55)' }}>
            {ex.city}{ex.state ? ', ' + ex.state : ''}
          </p>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2.5">
          {ex.price && <span className="font-bold text-base" style={{ ...serif, color: C.text }}>${Math.round(ex.price / 1000)}K</span>}
          <div className="flex items-center gap-2 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>
            {ex.beds && <span>{ex.beds}bd</span>}
            {ex.baths && <span>{ex.baths}ba</span>}
          </div>
        </div>

        {reasons.slice(0, 2).map((r, i) => (
          <div key={i} className="flex items-start gap-1.5 mb-0.5" style={{ ...sans, color: C.green, fontSize: '0.75rem' }}>
            <span style={{ fontSize: '0.5rem', marginTop: '0.35rem', flexShrink: 0, color: '#34D399' }}>&#10004;</span>{r}
          </div>
        ))}

        {imp.source_url && (
          <a href={imp.source_url} target="_blank" rel="noopener noreferrer"
            className="text-xs underline mt-2 block transition-opacity hover:opacity-70"
            style={{ ...sans, color: 'rgba(196,168,122,0.5)', textDecorationColor: 'rgba(196,168,122,0.25)' }}>
            View original listing ↗
          </a>
        )}

        <div className="flex gap-2 mt-3 pt-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={() => onDelete(imp.id)}
            data-testid={'delete-imported-' + imp.id}
            className="flex-1 py-2 rounded-full text-xs transition-all"
            style={{ ...sans, color: 'rgba(200,211,223,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}>
            Remove
          </button>
        </div>
      </div>
    </motion.div>
  );
}


function SavedCard({ listing, onUnsave, onView, onCompare, compareMode, isSelectedForCompare }) {
  const [showShare, setShowShare] = useState(false);
  const reasons   = listing.match_reasons   || [];
  const conflicts = listing.match_conflicts || [];
  const photo     = listing.photos?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80';

  const borderStyle = isSelectedForCompare
    ? '1px solid rgba(196,168,122,0.5)'
    : compareMode
    ? '1px solid rgba(255,255,255,0.06)'
    : '1px solid ' + C.border;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl overflow-hidden cursor-pointer group relative"
        style={{
          background: C.surface,
          border: borderStyle,
          boxShadow: isSelectedForCompare ? '0 0 0 2px rgba(196,168,122,0.25), 0 8px 32px rgba(0,0,0,0.4)' : 'none',
          opacity: compareMode && !isSelectedForCompare ? 0.65 : 1,
          transition: 'opacity 0.2s, border 0.2s, box-shadow 0.2s',
        }}
        data-testid={'saved-card-' + listing.id}
        onClick={!compareMode ? onView : (isSelectedForCompare ? undefined : () => onCompare(listing))}>

        {isSelectedForCompare && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ ...sans, background: 'rgba(196,168,122,0.18)', border: '1px solid rgba(196,168,122,0.45)', color: C.gold, backdropFilter: 'blur(8px)' }}>
            Selected
          </div>
        )}

        {/* Photo */}
        <div className="relative overflow-hidden" style={{ height: '180px' }}>
          <img src={photo} alt={listing.address}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'; }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(14,21,32,0.92) 0%, transparent 55%)' }} />
          <div className="absolute bottom-3 left-4">
            <p className="font-semibold text-sm" style={{ ...sans, color: C.text }}>{listing.address}</p>
            <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.6)' }}>{listing.city}, {listing.state}</p>
          </div>
        </div>

        <div className="px-4 pt-3 pb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-bold" style={{ ...serif, color: C.text }}>{formatPrice(listing.price)}</span>
            <div className="flex items-center gap-2.5 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.55)' }}>
              <span className="flex items-center gap-1"><BedDouble size={10} />{listing.beds}</span>
              <span className="flex items-center gap-1"><Bath size={10} />{listing.baths}</span>
            </div>
          </div>

          {reasons.length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5" style={{ ...sans, color: 'rgba(52,211,153,0.4)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Why you liked this</p>
              {reasons.slice(0, 2).map((r, i) => (
                <div key={i} className="flex items-center gap-1.5 mb-0.5" style={{ ...sans, color: C.green, fontSize: '0.78rem' }}>
                  <span style={{ color: '#34D399', fontSize: '0.5rem' }}>&#10004;</span>{r}
                </div>
              ))}
            </div>
          )}
          {conflicts.length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5" style={{ ...sans, color: 'rgba(251,191,36,0.38)', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Still missing</p>
              {conflicts.slice(0, 1).map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 mb-0.5" style={{ ...sans, color: C.amber, fontSize: '0.78rem' }}>
                  <span style={{ color: '#FBBF24', fontSize: '0.5rem' }}>&#9702;</span>{c}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {compareMode ? (
              <button
                onClick={e => { e.stopPropagation(); onCompare(listing); }}
                data-testid={'compare-pick-' + listing.id}
                className="flex-1 py-2 rounded-full text-xs font-semibold transition-all"
                style={{
                  ...sans,
                  background: isSelectedForCompare ? 'rgba(196,168,122,0.15)' : 'rgba(255,255,255,0.06)',
                  color: isSelectedForCompare ? C.gold : C.muted,
                  border: isSelectedForCompare ? '1px solid rgba(196,168,122,0.35)' : '1px solid rgba(255,255,255,0.1)',
                }}>
                {isSelectedForCompare ? 'Deselect' : 'Compare with this'}
              </button>
            ) : (
              <>
                <button onClick={e => { e.stopPropagation(); onView(); }}
                  data-testid={'view-saved-' + listing.id}
                  className="flex-1 py-2 rounded-full text-xs font-semibold transition-all hover:opacity-90"
                  style={{ ...sans, background: C.coral, color: '#fff' }}>
                  View details
                </button>
                <button onClick={e => { e.stopPropagation(); onCompare(listing); }}
                  data-testid={'compare-btn-' + listing.id}
                  className="px-3 py-2 rounded-full text-xs font-medium transition-all"
                  style={{ ...sans, color: 'rgba(200,211,223,0.55)', border: '1px solid rgba(255,255,255,0.1)' }}
                  title="Compare homes">
                  Compare
                </button>
                <button onClick={e => { e.stopPropagation(); setShowShare(true); }}
                  className="px-3 py-2 rounded-full text-xs transition-all hover:bg-white/8"
                  style={{ ...sans, color: 'rgba(200,211,223,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <Share2 size={13} />
                </button>
                <button onClick={e => { e.stopPropagation(); onUnsave(listing.id); }}
                  className="px-3 py-2 rounded-full text-xs transition-all hover:bg-white/8"
                  style={{ ...sans, color: 'rgba(200,211,223,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Remove
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showShare && <ShareSheet listing={listing} onClose={() => setShowShare(false)} />}
      </AnimatePresence>
    </>
  );
}

// ─── Discovery ─────────────────────────────────────────────────────────────────
const STAGES = [
  { id: 'discovering',    label: 'Discovering',    short: 'Exploring' },
  { id: 'narrowing',      label: 'Narrowing',      short: 'Shortlisting' },
  { id: 'touring',        label: 'Touring',        short: 'Touring' },
  { id: 'ready_to_offer', label: 'Ready to Offer', short: 'Offering' },
];

function JourneyStageBar({ stage }) {
  const activeIdx = STAGES.findIndex(s => s.id === stage);
  const safeIdx = activeIdx < 0 ? 0 : activeIdx;
  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="journey-stage-bar">
      <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-3">Your Buyer Journey</p>
      <div className="flex items-center gap-0">
        {STAGES.map((s, i) => {
          const done    = i < safeIdx;
          const current = i === safeIdx;
          const future  = i > safeIdx;
          return (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center" style={{ flex: 1 }}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black mb-1.5 transition-all"
                  style={current
                    ? { background: '#10B981', color: 'white', boxShadow: '0 0 0 3px rgba(16,185,129,0.25)' }
                    : done
                      ? { background: 'rgba(16,185,129,0.3)', color: '#10B981' }
                      : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.2)' }}>
                  {done ? <CheckCircle size={12} /> : i + 1}
                </div>
                <span className="text-xs font-semibold text-center leading-tight hidden sm:block"
                  style={{ color: current ? '#10B981' : done ? 'rgba(16,185,129,0.6)' : 'rgba(255,255,255,0.2)' }}>
                  {s.label}
                </span>
                <span className="text-xs font-semibold text-center leading-tight sm:hidden"
                  style={{ color: current ? '#10B981' : done ? 'rgba(16,185,129,0.6)' : 'rgba(255,255,255,0.2)' }}>
                  {s.short}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <div className="h-0.5 flex-shrink-0 mx-1 rounded-full transition-all" style={{
                  width: 24,
                  background: i < safeIdx ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.08)',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 rounded-full animate-spin"
        style={{ border: '3px solid #0A192F', borderTopColor: 'transparent' }} />
    </div>
  );
}

function DiscoverTab() {
  const { authFetch }  = useAuth();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const openRefinement = searchParams.get('type') === 'open';
  const [listings,    setListings]    = useState([]);
  const [savedIds,    setSavedIds]    = useState(new Set());
  const [loading,     setLoading]     = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [profileSet,  setProfileSet]  = useState(false);
  const [showWizard,  setShowWizard]  = useState(false);
  const [currentIdx,  setCurrentIdx]  = useState(0);
  const [usageState,  setUsageState]  = useState(getUsage);
  const [paywallTrigger, setPaywallTrigger] = useState(null);
  const [showRefinement, setShowRefinement] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, savedRes, profileRes] = await Promise.all([
        authFetch(`${API}/api/listings/discover`),
        authFetch(`${API}/api/buyer/saved`),
        authFetch(`${API}/api/buyer/profile`),
      ]);
      if (listRes.ok) setListings(await listRes.json());
      if (savedRes.ok) { const s = await savedRes.json(); setSavedIds(new Set(s.map(l => l.id))); }
      if (profileRes.ok) {
        const p = await profileRes.json();
        const hasLegacy    = !!(p.location_city || p.max_price || p.min_beds);
        const hasCanonical = p.schema_version === 2 || !!(p.core_attributes || p.budget?.max_price);
        setProfileSet(hasLegacy || hasCanonical);
        if (!hasLegacy && !hasCanonical && Object.keys(p).length === 0) setShowWizard(true);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaveToggle = async (listingId, isSaved) => {
    const method = isSaved ? 'DELETE' : 'POST';
    await authFetch(`${API}/api/buyer/save/${listingId}`, { method });
    setSavedIds(prev => { const n = new Set(prev); isSaved ? n.delete(listingId) : n.add(listingId); return n; });
  };

  const handleFeedback = async (listingId, liked, reason) => {
    try {
      await authFetch(`${API}/api/listings/${listingId}/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ liked, reason }),
      });
    } catch (e) { console.error(e); }
  };

  const handleDismissDiscover = useCallback((id) => {
    setListings(prev => prev.filter(l => l.id !== id));
    setCurrentIdx(0);
  }, []);

  const visible = isPremium() ? listings : listings.slice(0, FREE_LIMITS.views);
  const current = visible[currentIdx];
  const microcopy = MICROCOPY[Math.min(currentIdx, MICROCOPY.length - 1)];
  const progress  = visible.length ? Math.round(((currentIdx + 1) / visible.length) * 100) : 0;

  if (showWizard) return <BuyerIntakeWizard onComplete={() => { setShowWizard(false); fetchAll(); }} />;
  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ minHeight: '80vh' }}>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <motion.p key={microcopy} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="text-xs italic" style={{ ...sans, color: 'rgba(196,168,122,0.65)' }}>
            {microcopy}
          </motion.p>
          <p className="text-sm font-semibold mt-0.5" style={{ ...sans, color: C.muted }}>
            {visible.length > 0 ? `${currentIdx + 1} of ${visible.length}` : 'No matches yet'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!profileSet && (
            <button onClick={() => setShowWizard(true)} data-testid="setup-profile-btn"
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl text-white"
              style={{ ...sans, background: C.coral }}>
              <Wand2 size={13} /> Tell us your type
            </button>
          )}
          <button onClick={() => setShowRefinement(s => !s)}
            className="text-xs px-3 py-1.5 rounded-full transition-all"
            style={{ ...sans, color: C.muted, border: '1px solid ' + C.border }}>
            Refine
          </button>
        </div>
      </div>

      {visible.length > 0 && (
        <div className="mb-5 h-px rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <motion.div className="h-full rounded-full" animate={{ width: progress + '%' }}
            transition={{ duration: 0.5 }} style={{ background: C.gold }} />
        </div>
      )}

      {showRefinement && <RefinementPanel onApply={() => { fetchAll(); setShowRefinement(false); }} defaultOpen />}

      {current ? (
        <div className="max-w-md mx-auto">
          <AnimatePresence mode="wait">
            <motion.div key={current.id}
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, x: -30 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
              <ListingCard
                listing={current}
                isSaved={savedIds.has(current.id)}
                onSaveToggle={(id, isSaved) => {
                  if (!isPremium()) { setPaywallTrigger('save'); return; }
                  handleSaveToggle(id, isSaved);
                }}
                onShowScore={setActiveModal}
                onFeedback={(id, liked, reason) => {
                  handleFeedback(id, liked, reason);
                  if (reason === 'ring_high_intent') {
                    const u = getUsage();
                    if (!isPremium() && u.justRight >= FREE_LIMITS.justRight) { setPaywallTrigger('justright'); return; }
                    const next = { ...u, justRight: u.justRight + 1 };
                    setUsage(next); setUsageState(next);
                    setSaveType(id, 'justright');
                  }
                  if (reason === 'close') {
                    if (!isPremium()) { setPaywallTrigger('save'); return; }
                    setSaveType(id, 'close');
                  }
                }}
                onDismiss={handleDismissDiscover}
                onView={() => navigate(`/listing/${current.id}`, { state: { listing: current } })}
              />
            </motion.div>
          </AnimatePresence>
          {currentIdx < visible.length - 1 && (
            <div className="mt-3 text-center">
              <button onClick={() => setCurrentIdx(i => i + 1)}
                style={{ ...sans, color: 'rgba(200,211,223,0.3)', background: 'none', border: 'none', fontSize: '0.72rem' }}>
                Skip to next →
              </button>
            </div>
          )}
          {!isPremium() && currentIdx >= FREE_LIMITS.views - 3 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="mt-5 p-4 rounded-2xl text-center"
              style={{ background: 'rgba(196,168,122,0.07)', border: '1px solid rgba(196,168,122,0.18)' }}>
              <p className="text-xs font-medium mb-1" style={{ ...sans, color: C.gold }}>You're running out of free matches.</p>
              <Link to="/upgrade" className="text-xs underline" style={{ ...sans, color: 'rgba(196,168,122,0.6)' }}>
                Unlock unlimited browsing →
              </Link>
            </motion.div>
          )}
        </div>
      ) : (
        <div className="p-12 rounded-2xl text-center max-w-md mx-auto"
          style={{ background: C.surface, border: '1px dashed ' + C.border }}>
          <Compass size={24} className="mx-auto mb-2" style={{ color: 'rgba(200,211,223,0.4)' }} />
          <p className="text-sm font-semibold mb-1" style={{ ...sans, color: C.text }}>You've seen all your matches</p>
          <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>New homes are added daily</p>
        </div>
      )}

      <MatchScoreModal listing={activeModal} onClose={() => setActiveModal(null)} />
      <AnimatePresence>
        {paywallTrigger && <PaywallModal trigger={paywallTrigger} onClose={() => setPaywallTrigger(null)} />}
      </AnimatePresence>
    </div>
  );
}


function SavedTab() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('justright');
  const [compareFirst, setCompareFirst] = useState(null);
  const [showImport, setShowImport]   = useState(false);
  const [imported, setImported]       = useState([]);

  useEffect(() => {
    authFetch(`${API}/api/buyer/saved`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setSaved(data); setLoading(false); })
      .catch(() => setLoading(false));
    authFetch(`${API}/api/buyer/imports`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setImported(data))
      .catch(() => {});
  }, [authFetch]);

  const handleUnsave = async (id) => {
    await authFetch(`${API}/api/buyer/save/${id}`, { method: 'DELETE' });
    setSaved(prev => prev.filter(l => l.id !== id));
    if (compareFirst?.id === id) setCompareFirst(null);
  };

  const handleCompare = (listing) => {
    if (!compareFirst) {
      setCompareFirst(listing);
    } else if (compareFirst.id === listing.id) {
      setCompareFirst(null);
    } else {
      navigate('/compare', { state: { homeA: compareFirst, homeB: listing } });
    }
  };

  const handleImported = (result) => {
    setImported(prev => [result, ...prev]);
  };

  const saveTypes = getSaveTypes();
  const justRight = saved.filter(l => saveTypes[l.id] === 'justright' || !saveTypes[l.id]);
  const close     = saved.filter(l => saveTypes[l.id] === 'close');
  const shown     = activeTab === 'justright' ? justRight : close;
  const compareMode = !!compareFirst;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-7">
        <div>
          <h2 className="text-2xl font-bold mb-1" style={{ ...serif, color: C.text }}>Your matches</h2>
          <p className="text-sm italic" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>The ones that felt right.</p>
        </div>
        <button
          onClick={() => setShowImport(true)}
          data-testid="add-home-btn"
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold transition-all flex-shrink-0"
          style={{ ...sans, background: 'rgba(196,168,122,0.1)', color: C.gold, border: '1px solid rgba(196,168,122,0.25)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(196,168,122,0.17)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(196,168,122,0.1)'; }}>
          <Plus size={13} />
          Add a home
        </button>
      </div>

      {saved.length === 0 ? (
        <div className="p-12 rounded-2xl text-center max-w-md mx-auto"
          style={{ background: C.surface, border: '1px dashed ' + C.border }}>
          <Gem size={22} className="mx-auto mb-3" style={{ color: 'rgba(196,168,122,0.4)' }} />
          <p className="font-semibold text-sm mb-1" style={{ ...sans, color: C.text }}>No saved matches yet</p>
          <p className="text-xs leading-relaxed" style={{ ...sans, color: 'rgba(200,211,223,0.4)' }}>
            Mark homes as "Just right" or "Close" while browsing.<br />
            They'll live here, waiting.
          </p>
        </div>
      ) : (
        <>
          {/* Compare selection banner */}
          <AnimatePresence>
            {compareMode && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="mb-5 px-5 py-3.5 rounded-2xl flex items-center justify-between gap-4"
                style={{ background: 'rgba(196,168,122,0.09)', border: '1px solid rgba(196,168,122,0.25)' }}
                data-testid="compare-banner">
                <div>
                  <p className="text-xs font-semibold" style={{ ...sans, color: C.gold }}>
                    {compareFirst.address}
                  </p>
                  <p className="text-xs mt-0.5" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>
                    Now select a second home to compare
                  </p>
                </div>
                <button
                  onClick={() => setCompareFirst(null)}
                  className="text-xs px-3 py-1.5 rounded-full transition-all"
                  style={{ ...sans, color: 'rgba(200,211,223,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid="cancel-compare-btn">
                  Cancel
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-2 mb-6">
            {[
              { key: 'justright', label: 'Just right', count: justRight.length },
              { key: 'close',     label: 'Close',      count: close.length     },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all"
                style={{
                  ...sans,
                  background: activeTab === t.key ? (t.key === 'justright' ? 'rgba(196,168,122,0.15)' : 'rgba(255,255,255,0.08)') : 'transparent',
                  color:      activeTab === t.key ? (t.key === 'justright' ? C.gold : C.text) : 'rgba(200,211,223,0.45)',
                  border:     activeTab === t.key ? ('1px solid ' + (t.key === 'justright' ? 'rgba(196,168,122,0.3)' : 'rgba(255,255,255,0.12)')) : '1px solid transparent',
                }}>
                {t.label}
                {t.count > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs"
                    style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(200,211,223,0.6)' }}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm italic mb-1" style={{ ...sans, color: 'rgba(200,211,223,0.35)' }}>
                {activeTab === 'justright'
                  ? 'Nothing marked "Just right" yet.'
                  : 'Nothing marked "Close" yet.'}
              </p>
              <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.22)' }}>
                Keep browsing — the right one is out there.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {shown.map(l => (
                <SavedCard key={l.id} listing={l}
                  onUnsave={handleUnsave}
                  onView={() => navigate(`/listing/${l.id}`, { state: { listing: l } })}
                  onCompare={handleCompare}
                  compareMode={compareMode}
                  isSelectedForCompare={compareFirst?.id === l.id}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Homes you added section */}
      {imported.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '2rem' }}>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ ...sans, color: C.text }}>Homes you added</p>
              <p className="text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.4)' }}>Found elsewhere, scored by Goldielocks.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {imported.map(imp => (
              <ImportedCard key={imp.id} imported={imp} onDelete={async (id) => {
                await authFetch(`${API}/api/buyer/imports/${id}`, { method: 'DELETE' });
                setImported(prev => prev.filter(i => i.id !== id));
              }} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state with add prompt */}
      {saved.length === 0 && imported.length === 0 && (
        <div className="mt-8 p-6 rounded-2xl text-center"
          style={{ background: 'rgba(196,168,122,0.05)', border: '1px dashed rgba(196,168,122,0.2)' }}>
          <p className="text-xs font-medium mb-1.5" style={{ ...sans, color: 'rgba(196,168,122,0.6)' }}>
            Found a home on Zillow or Redfin?
          </p>
          <p className="text-xs mb-3" style={{ ...sans, color: 'rgba(200,211,223,0.4)' }}>
            Paste the link. We'll tell you if it actually fits.
          </p>
          <button onClick={() => setShowImport(true)}
            data-testid="add-home-empty-btn"
            className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
            style={{ ...sans, background: 'rgba(196,168,122,0.12)', color: C.gold, border: '1px solid rgba(196,168,122,0.3)' }}>
            + Add a home you found elsewhere
          </button>
        </div>
      )}

      {/* Import modal */}
      <AnimatePresence>
        {showImport && (
          <ImportHomeModal
            onClose={() => setShowImport(false)}
            authFetch={authFetch}
            onImported={handleImported}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function ProfileTab() {
  const { authFetch } = useAuth();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  useEffect(() => {
    authFetch(`${API}/api/buyer/profile`)
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        if (!data || Object.keys(data).length === 0) {
          setInitialData(null);
        } else if (data.schema_version === 2 || data.core_attributes) {
          // Already v2 canonical — pass directly
          setInitialData(data);
        } else {
          // Map v1 legacy → v2 wizard format
          setInitialData({
            intent: 'buy',
            location: {
              cities: data.location_city ? [data.location_city] : [],
              radius_miles: data.radius_miles || 25,
            },
            budget: {
              min_price: data.min_price || '',
              max_price: data.max_price || '',
              flexibility_pct: 5,
              not_sure: false,
            },
            core_attributes: {
              property_types: [],
              min_beds: data.min_beds || 3,
              min_baths: data.min_baths || 2,
              min_sqft: data.min_sqft || '',
              max_sqft: '',
            },
            feature_flags: {
              must_have: data.must_have_features || [],
              nice_to_have: [],
            },
            land_attributes: { min_usable_land_percent: '', zoning_types: [], horse_property: false },
            utilities: { solar_preference: 'any', sewer_type: 'any', internet_type: 'any' },
            financial_attributes: { max_hoa: '', str_allowed_required: false, income_potential_required: false },
            lifestyle_tags: [],
            negative_flags: data.dealbreaker_features || [],
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [authFetch]);

  if (loading) return <LoadingSpinner />;

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ background: '#F0FDF4' }}>
          <CheckCircle size={32} className="text-emerald-500" />
        </div>
        <h2 className="text-xl font-black mb-1" style={{ color: '#0A192F' }}>Profile Updated!</h2>
        <p className="text-slate-500 text-sm mb-6">Your match scores will now reflect your preferences.</p>
        <button onClick={() => setDone(false)} data-testid="edit-profile-again-btn"
          className="flex items-center gap-1.5 text-sm font-bold px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all">
          <Wand2 size={14} />Edit Again
        </button>
      </div>
    );
  }

  return (
    <BuyerIntakeWizard
      initialData={initialData}
      onComplete={() => setDone(true)}
    />
  );
}

export default function BuyerDashboard({ tab }) {
  const { user, authFetch } = useAuth();
  const [journeyStage, setJourneyStage] = useState('discovering');
  const [showFloatingImport, setShowFloatingImport] = useState(false);

  useEffect(() => {
    authFetch(`${API}/api/buyer/profile`)
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        const stage = data?.behavioral_signals?.journey_stage;
        if (stage) setJourneyStage(stage);
      })
      .catch(() => {});
  }, [authFetch, tab]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div className="mb-8 p-5 rounded-2xl"
        style={{ background: 'linear-gradient(135deg, #07090F 0%, #10141E 100%)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-4 mb-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(224,122,95,0.15)', border: '1px solid rgba(224,122,95,0.3)' }}>
            <User size={18} style={{ color: '#E07A5F' }} />
          </div>
          <div>
            <p className="font-bold text-sm" style={{ color: '#F8F9FA' }}>Welcome back, {user?.name}</p>
            <p className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>Your matches are ready 💘</p>
          </div>
        </div>
        <JourneyStageBar stage={journeyStage} />
      </div>

      {tab === 'discover' && <DiscoverTab />}
      {tab === 'saved' && <SavedTab />}
      {tab === 'profile' && <ProfileTab />}

      {/* Floating "+ Add a home" button — visible during discovery */}
      {tab === 'discover' && (
        <>
          <motion.button
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            onClick={() => setShowFloatingImport(true)}
            data-testid="floating-add-home-btn"
            className="fixed bottom-7 right-6 flex items-center gap-2 px-4 py-3 rounded-full font-semibold text-xs shadow-xl transition-all z-40"
            style={{
              ...sans,
              background: 'rgba(22,32,46,0.95)',
              color: C.gold,
              border: '1px solid rgba(196,168,122,0.3)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              letterSpacing: '0.02em',
            }}
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.97 }}>
            <Plus size={13} /> Add a home
          </motion.button>

          <AnimatePresence>
            {showFloatingImport && (
              <ImportHomeModal
                onClose={() => setShowFloatingImport(false)}
                authFetch={authFetch}
                onImported={() => {}}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
